import { fetchAuthSession } from "aws-amplify/auth";
import { useEffect, useState } from "react";

export interface CensusSession {
  /** False until the one-shot Cognito session probe has resolved. */
  checked: boolean;
  isAuthenticated: boolean;
  /** Account email from the ID token, when signed in. */
  email: string;
  /**
   * Whether the signed-in account may run the census on the company's behalf —
   * i.e. it's the business OWNER or a MANAGER. The flow is owner/manager-only
   * (a regular employee can't quote for the company), so anyone else is shown an
   * explainer instead of the form. False for unauthenticated and until the
   * session resolves.
   */
  canManage: boolean;
}

/** Identity we extract from a resolved Cognito ID token. */
export interface CensusIdentity {
  isAuthenticated: boolean;
  email: string;
  canManage: boolean;
}

/**
 * Access roles allowed to run the census on a company's behalf: the business
 * OWNER or a MANAGER, plus self-serve PROSPECT signups (who exist only to run
 * the census — they're blocked from cockpit/wallet by their group).
 */
const MANAGE_ROLES = new Set(["owner", "manager", "prospect"]);

/**
 * Read the access role from an ID-token payload. Cognito carries it either as a
 * `custom:role` claim or as Cognito groups, so we check both (case-insensitive)
 * and treat `owner` or `manager` in either place as allowed.
 */
export function canManageFromIdToken(payload: Record<string, unknown> | undefined): boolean {
  if (!payload) return false;
  const roles: string[] = [];
  const role = payload["custom:role"];
  if (typeof role === "string") roles.push(role);
  const groups = payload["cognito:groups"];
  if (Array.isArray(groups)) {
    for (const g of groups) if (typeof g === "string") roles.push(g);
  } else if (typeof groups === "string") {
    roles.push(groups);
  }
  return roles.some((r) => MANAGE_ROLES.has(r.toLowerCase()));
}

/**
 * Resolve the current Cognito identity (email + manage access) from Amplify's
 * persisted tokens. Used both by the on-mount session probe and right after a
 * fresh login, so the access check works without a page refresh. Resolves to an
 * anonymous identity when there's no session.
 */
export async function fetchCensusIdentity(): Promise<CensusIdentity> {
  try {
    const result = await fetchAuthSession();
    const payload = result.tokens?.idToken?.payload as Record<string, unknown> | undefined;
    const email = typeof payload?.email === "string" ? payload.email : "";
    return {
      isAuthenticated: Boolean(result.tokens?.idToken),
      email,
      canManage: canManageFromIdToken(payload),
    };
  } catch {
    return { isAuthenticated: false, email: "", canManage: false };
  }
}

/**
 * Probe for an existing Cognito session once on mount.
 *
 * The flow keeps its step state in memory, so a refresh would otherwise drop
 * the logged-in user back to `/login` (and a re-login then throws
 * "already a signed in user"). This restores the session like the other apps:
 * `fetchAuthSession()` reads Amplify's persisted tokens without needing an
 * `<Authenticator.Provider>`. No session simply resolves to anonymous.
 */
export function useCensusSession(): CensusSession {
  const [session, setSession] = useState<CensusSession>({
    checked: false,
    isAuthenticated: false,
    email: "",
    canManage: false,
  });

  useEffect(() => {
    let cancelled = false;
    fetchCensusIdentity().then((identity) => {
      if (!cancelled) setSession({ checked: true, ...identity });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return session;
}
